/**
 * Wirtschaftspläne-Sektion im WEG-Property-Tab.
 * Listet alle Wirtschaftspläne des Objekts (nach Jahr absteigend).
 * Anlegen erzeugt einen Draft und navigiert zum Editor.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { FileText, Plus, ArrowRight, Coins } from "@phosphor-icons/react";

export default function WirtschaftsplanSection({ property }) {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const [title, setTitle] = useState("");

  const load = () =>
    api.get(`/weg/${property.id}/economic-plans`)
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, [property.id]);

  const create = async () => {
    try {
      const r = await api.post(`/weg/${property.id}/economic-plans`, {
        property_id: property.id, year: Number(year),
        title: title.trim() || `Wirtschaftsplan ${year}`,
        positions: [],
      });
      toast.success(`Entwurf ${year} angelegt`);
      setOpen(false); setTitle("");
      nav(`/weg/wirtschaftsplan/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Anlegen");
    }
  };

  return (
    <div className="space-y-3" data-testid="wp-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <FileText weight="duotone" className="text-emerald-700" /> Wirtschaftspläne
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Jahres-Wirtschaftspläne pro WEG-Objekt. Positionen mit 4 wählbaren Verteilungsschlüsseln.{" "}
            <Link to="/weg/konten" className="text-emerald-700 hover:underline inline-flex items-center gap-1">
              <Coins size={11} /> Kontenrahmen
            </Link>
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="wp-add-btn">
              <Plus size={13} className="mr-1.5" /> Neuer Wirtschaftsplan
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Neuer Wirtschaftsplan</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <label className="text-xs text-slate-600 block">Jahr *
                <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="wp-year-input" />
              </label>
              <label className="text-xs text-slate-600 block">Bezeichnung
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`Wirtschaftsplan ${year}`} data-testid="wp-title-input" />
              </label>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button onClick={create} data-testid="wp-create-btn">Anlegen &amp; öffnen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Lade …</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center bg-slate-50 text-sm text-slate-500" data-testid="wp-empty">
          Noch kein Wirtschaftsplan angelegt.
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.id} data-testid={`wp-item-${r.year}`}>
              <Link
                to={`/weg/wirtschaftsplan/${r.id}`}
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 hover:border-emerald-400 hover:shadow-sm transition"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-semibold text-slate-900">{r.year}</div>
                    <Badge className={`text-[10px] ${r.status === "approved" ? "bg-emerald-100 text-emerald-800" : r.status === "closed" ? "bg-slate-100 text-slate-700" : "bg-amber-100 text-amber-800"}`}>
                      {r.status === "draft" ? "Entwurf" : r.status === "approved" ? "Genehmigt" : "Geschlossen"}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.title || `Wirtschaftsplan ${r.year}`} · {r.position_count || 0} Positionen
                  </div>
                </div>
                <ArrowRight size={16} className="text-slate-400" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
